import { useState } from 'react';

import { ChevronDown, ChevronUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { Collapse } from 'react-collapse';

import { Button, Divider, Skeleton, Spinner } from 'src/components';

import { useLingui, Trans } from '@lingui/react/macro';
import type {
  CheckoutPrice,
  Price,
  TrialOffer,
} from 'src/api/endpoints/cloudConnect.types';
import { PriceConnect } from 'src/components/PriceConnect';
import { useFreeTrialLengthText } from 'src/utils/freeTrialLength';
import {
  PromotionSummary,
  type AppliedPromotionSummary,
} from './PromotionSummary';
import { formatBillingStartDate } from './util';

type SummaryProps = {
  trialOffer: TrialOffer | null | undefined;
  price: Price | undefined;
  checkoutPrice: CheckoutPrice | undefined;
  isLoading: boolean;
  isLoadingPrice: boolean;
  showTaxBreakdown: boolean;
  promotion?: AppliedPromotionSummary;
};

const toCheckoutPrice = (price: CheckoutPrice): Price => ({
  currency: price.currency,
  precision: price.precision,
  quantity: 1,
  totalAmount: price.totalAmount,
  unitAmount: price.totalAmount,
});

// Summary is the component that displays the summary of the connect offering,
// which includes price, dates and so on.
// It supports both trial and non-trial offerings.
export function Summary({
  trialOffer,
  price,
  checkoutPrice,
  isLoading,
  isLoadingPrice,
  showTaxBreakdown,
  promotion,
}: SummaryProps) {
  if (isLoading) {
    return <Spinner />;
  }

  // An applied promotion replaces the free-trial offer.
  if (promotion) {
    return (
      <PromotionSummary promotion={promotion} isLoadingPrice={isLoadingPrice} />
    );
  }

  // If no promotion code is applied, we show the free-trial offer if it exists.
  if (trialOffer) {
    return (
      <DetailsWithTrial
        offer={trialOffer}
        price={price}
        checkoutPrice={checkoutPrice}
        isLoadingPrice={isLoadingPrice}
      />
    );
  }

  // Otherwise we show the regular price details.
  return (
    <Details
      price={price}
      checkoutPrice={checkoutPrice}
      isLoadingPrice={isLoadingPrice}
      showTaxBreakdown={showTaxBreakdown}
    />
  );
}

function DetailsWithTrial({
  offer,
  price,
  checkoutPrice,
  isLoadingPrice,
}: {
  offer: TrialOffer;
  price: Price | undefined;
  checkoutPrice: CheckoutPrice | undefined;
  isLoadingPrice: boolean;
}) {
  const { t, i18n } = useLingui();
  const [showDetails, setShowDetails] = useState(false);

  const freeTrialLengthDays = offer.durationDays;
  const freeTrialLengthDisplay = useFreeTrialLengthText(freeTrialLengthDays);
  const billingStartDate = formatBillingStartDate(
    offer.durationDays,
    i18n.locale
  );
  const billingStartsText = t`Billing starts ${billingStartDate}`;
  const reminderText = t`We’ll send you a reminder email before your free trial ends.`;

  const checkoutDisplayPrice = checkoutPrice
    ? toCheckoutPrice(checkoutPrice)
    : undefined;
  const zeroPrice = checkoutDisplayPrice
    ? { ...checkoutDisplayPrice, totalAmount: 0, unitAmount: 0 }
    : price
      ? { ...price, totalAmount: 0 }
      : undefined;
  const billingPrice = checkoutDisplayPrice ?? price;

  return (
    <div>
      <div className="flex w-full flex-col bg-neutral-light-3 p-8 ll:hidden">
        <Button
          variant="tertiary-neutral"
          className="w-full bg-none"
          onClick={() => setShowDetails((showDetails) => !showDetails)}
          data-cy="order-summary-toggle"
          aria-expanded={showDetails}
        >
          <span className="flex w-full items-center justify-between">
            <span className="mr-auto">{freeTrialLengthDisplay}</span>
            <span>
              <Skeleton isLoading={isLoadingPrice}>
                <b data-cy="connect-plan-row-price">
                  <PriceConnect price={zeroPrice} />
                </b>
              </Skeleton>
            </span>
            {showDetails ? (
              <ChevronUp className="ml-16 inline max-w-20" />
            ) : (
              <ChevronDown className="ml-16 inline max-w-20" />
            )}
          </span>
        </Button>
        <Collapse isOpened={showDetails}>
          <Divider className="my-16" />
          <div
            className="flex flex-col bg-neutral-light-3 px-16 pb-16"
            data-cy="order-summary-details"
          >
            <div className="flex justify-between" data-cy="billing-starts-row">
              <p data-cy="billing-start-date" className="my-4">
                {billingStartsText}
              </p>
              <p className="my-4">
                <Skeleton isLoading={isLoadingPrice}>
                  <PriceConnect price={billingPrice} />
                </Skeleton>
              </p>
            </div>
            <Typography variant="body-sm" className="mt-16">
              {reminderText}
            </Typography>
          </div>
        </Collapse>
      </div>

      <div className="hidden flex-col bg-neutral-light-3 p-20 ll:flex">
        <>
          <div data-cy="connect-plan-row" className="flex justify-between">
            <p className="my-4 font-semibold">{freeTrialLengthDisplay}</p>
            <p className="my-4">
              <b>
                <Skeleton isLoading={isLoadingPrice}>
                  <PriceConnect price={zeroPrice} />
                </Skeleton>
              </b>
            </p>
          </div>
          <div className="flex justify-between" data-cy="billing-starts-row">
            <p data-cy="billing-start-date" className="my-4">
              {billingStartsText}
            </p>
            <p className="my-4">
              <Skeleton isLoading={isLoadingPrice}>
                <PriceConnect price={billingPrice} />
              </Skeleton>
            </p>
          </div>
          <Typography variant="body-sm" className="mt-16">
            {reminderText}
          </Typography>
        </>
      </div>
    </div>
  );
}

function Details({
  price,
  checkoutPrice,
  isLoadingPrice,
  showTaxBreakdown,
}: {
  price: Price | undefined;
  checkoutPrice: CheckoutPrice | undefined;
  isLoadingPrice: boolean;
  showTaxBreakdown: boolean;
}) {
  const { t } = useLingui();
  const [showDetails, setShowDetails] = useState(false);

  const includesTax = checkoutPrice?.taxIncluded === true;
  const taxLabel =
    checkoutPrice?.taxType === 'VAT'
      ? t`VAT`
      : checkoutPrice?.taxType === 'GST'
        ? t`GST`
        : t`Sales tax`;

  const checkoutDisplayPrice = checkoutPrice
    ? toCheckoutPrice(checkoutPrice)
    : undefined;
  const subscriptionPrice = checkoutPrice
    ? {
        ...toCheckoutPrice(checkoutPrice),
        totalAmount: includesTax
          ? checkoutPrice.totalAmount
          : checkoutPrice.totalAmountExcludingTax,
        unitAmount: includesTax
          ? checkoutPrice.totalAmount
          : checkoutPrice.totalAmountExcludingTax,
      }
    : price;
  const taxPrice = checkoutPrice
    ? {
        ...toCheckoutPrice(checkoutPrice),
        totalAmount: checkoutPrice.taxAmount,
        unitAmount: checkoutPrice.taxAmount,
      }
    : undefined;
  const totalPrice = checkoutDisplayPrice ?? price;
  const hasTax = checkoutPrice?.taxType !== undefined;

  const details = (
    <div className="rounded-sm flex flex-col gap-8 p-16">
      <div className="flex justify-between gap-16">
        <Typography variant="body-lg" className="font-medium">
          <Trans>Connect subscription</Trans>
        </Typography>
        <Skeleton isLoading={isLoadingPrice} className="w-20">
          <Typography variant="body-lg" className="font-medium">
            <PriceConnect price={subscriptionPrice} hidePeriodLabel />
          </Typography>
        </Skeleton>
      </div>
      {showTaxBreakdown && !isLoadingPrice && hasTax && (
        <>
          <Divider className="my-0" />

          <div className="flex flex-row items-center justify-between">
            <p className="m-0 text-16">{taxLabel}</p>
            <Skeleton isLoading={isLoadingPrice} className="w-20">
              <p className="m-0 text-16">
                {includesTax ? (
                  <Trans
                    context="Tax"
                    comment="Whether tax is included in the price and not listed separately."
                  >
                    included
                  </Trans>
                ) : (
                  <PriceConnect price={taxPrice} hidePeriodLabel />
                )}
              </p>
            </Skeleton>
          </div>
          {includesTax ? null : (
            <>
              <Divider className="my-0" />
              <div className="flex flex-row items-center justify-between">
                <Typography variant="body-lg" className="font-semibold">
                  <Trans>Total</Trans>
                </Typography>
                <Typography variant="body-lg" className="font-semibold">
                  <PriceConnect price={totalPrice} hidePeriodLabel />
                </Typography>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex w-full flex-col bg-neutral-light-3 p-8 ll:hidden">
        <Button
          variant="tertiary-neutral"
          className="w-full bg-none"
          onClick={() => setShowDetails((showDetails) => !showDetails)}
          data-cy="order-summary-toggle"
          aria-expanded={showDetails}
        >
          <span className="flex w-full items-center justify-between">
            <Typography variant="body-md" className="inline">
              <Trans>Order summary:</Trans>
            </Typography>
            &nbsp;
            {showDetails ? null : (
              <Skeleton isLoading={isLoadingPrice} className="w-20">
                <Typography variant="body-md" className="ml-auto inline">
                  <PriceConnect
                    price={showTaxBreakdown ? totalPrice : subscriptionPrice}
                    hidePeriodLabel
                  />
                </Typography>
              </Skeleton>
            )}
            {showDetails ? (
              <ChevronUp className="ml-auto inline max-w-20" />
            ) : (
              <ChevronDown className="ml-auto inline max-w-20" />
            )}
          </span>
        </Button>
        <Collapse isOpened={showDetails}>
          <div data-cy="order-summary-details">{details}</div>
        </Collapse>
      </div>

      <div
        data-cy="connect-plan-row-price"
        className="hidden flex-col gap-16 bg-neutral-light-3 ll:flex"
      >
        {details}
      </div>
    </div>
  );
}
